// src/components/research/ResearchLinksMessages.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaFacebook, FaXTwitter, FaLink } from 'react-icons/fa6';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { Link } from 'react-router-dom';
import styles from './ResearchLinks.module.css';

const ResearchLinksMessages = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const pageUrl = window.location.href;

  useEffect(() => {
    async function fetchArticles() {
      try {
        const app = getApp();
        const db = getFirestore(app);
        const snapshot = await getDocs(collection(db, "researchArticles"));
        setArticles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error fetching articles:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchArticles();
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Intelligence Nodes & Dispatches",
    "description": "A live compendium of NOVIONIX Labs research nodes—insights and dispatches fueling our cognitive systems.",
    "url": pageUrl
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": articles.map((art, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "url": `${window.location.origin}/research/${art.id}`,
      "name": art.title
    }))
  };

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Intelligence Nodes & Dispatches | NOVIONIX Labs</title>
        <meta
          name="description"
          content="A live compendium of NOVIONIX Labs research nodes—insights and dispatches fueling our cognitive systems."
        />
        <meta
          name="keywords"
          content="NOVIONIX Labs, research nodes, cognitive insights, HIE intelligence"
        />
        <meta property="og:title" content="Intelligence Nodes & Dispatches | NOVIONIX Labs" />
        <meta
          property="og:description"
          content="Explore our curated intelligence nodes: research articles, insights, and system messages."
        />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/path/to/default/research-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Intelligence Nodes & Dispatches | NOVIONIX Labs" />
        <meta
          name="twitter:description"
          content="A live compendium of NOVIONIX Labs research nodes—insights and dispatches fueling our cognitive systems."
        />
        <meta name="twitter:image" content="/path/to/default/research-image.jpg" />
        <link rel="canonical" href={pageUrl} />

        <script type="application/ld+json">
          {JSON.stringify(pageSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema, null, 2)}
        </script>
      </Helmet>

      <div className={styles.meta}>
        <a
          href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
          className={styles.shareIcon}
          target="_blank"
          rel="noopener noreferrer"
          title="Share on Facebook"
        >
          <FaFacebook />
        </a>
        <a
          href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}`}
          className={styles.shareIcon}
          target="_blank"
          rel="noopener noreferrer"
          title="Tweet this page"
        >
          <FaXTwitter />
        </a>
        <button
          onClick={handleCopyLink}
          className={styles.shareIcon}
          title="Copy page link"
        >
          <FaLink />
        </button>
        {copySuccess && (
          <span className={styles.copyFeedback}>✅ URL Copied</span>
        )}
      </div>

      <h1 className={styles.title}>Intelligence Nodes & Dispatches</h1>
      <p className={styles.subtitle}>
        Dive into our curated compendium of research nodes—articles, insights, and system messages driving real-time AI.
      </p>

      {loading ? (
        <p className={styles.subtitle}>Synchronizing nodes…</p>
      ) : (
        <div className={styles.list}>
          {articles.map(article => (
            <div key={article.id} className={styles.card}>
              <div className={styles.cardHeader}>
                <Link
                  to={`/research/${article.id}`}
                  className={styles.cardTitle}
                >
                  {article.title}
                </Link>
              </div>
              <div className={styles.cardBody}>
                <p className={styles.cardSummary}>
                  {article.summary || `${article.message.slice(0, 150)}…`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResearchLinksMessages;
